import { useQueryClient } from '@tanstack/react-query';
import { useReleaseSlot } from '@hooks/useReleaseSlot';
import { useReleasePortReservation } from '@hooks/useReleasePortReservation';
import { selectQualifiedAddressId, useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import { getNonCouponItems } from '@hooks/useShoppingCart/coupon';
import { useSessionContext } from '@contexts/SessionContext';
import type { ShoppingCartDTO } from '@t/api.types';
import type { InstallationDetailsResponse } from '@t/checkout.types';

type UseReleaseOnCartEmptyReturn = {
  releaseIfCartEmpty: (cartOverride?: ShoppingCartDTO | null) => void;
};

export const useReleaseOnCartEmpty = (): UseReleaseOnCartEmptyReturn => {
  const queryClient = useQueryClient();
  const { sessionId } = useSessionContext();

  const { release: releaseSlot } = useReleaseSlot({
    onError: (err) => console.warn('[useReleaseOnCartEmpty] Slot release failed:', err),
  });

  const amsQualifiedAddressId = useQualificationFlowStore(selectQualifiedAddressId) ?? '';

  const { release: releasePort } = useReleasePortReservation({
    onError: (err) => console.warn('[useReleaseOnCartEmpty] Port release failed:', err),
  });

  const releaseIfCartEmpty = (cartOverride?: ShoppingCartDTO | null): void => {
    const cart = cartOverride ?? queryClient.getQueryData<ShoppingCartDTO>(['shoppingCart', sessionId]) ?? null;

    // A coupon-only cart counts as empty.
    if (getNonCouponItems(cart).length > 0) return;

    const installationData = queryClient.getQueryData<InstallationDetailsResponse>(['installationDetails', sessionId]);

    if (installationData?.installationDetails?.issueId && cart?.shoppingCartId) {
      releaseSlot(cart.shoppingCartId);
    }

    if (amsQualifiedAddressId && cart?.shoppingCartId) {
      releasePort(cart.shoppingCartId);
    }
  };

  return { releaseIfCartEmpty };
};
